// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * One globe's DSA 1.8 vector layers: loads them from a `.dsa`, runs their
 * §6 clock and draws them on the browser globe (vectorMapLayer.ts). There
 * is one player per globe (main.ts), so a second, third or fourth globe
 * draws its own dataset's layers.
 *
 * The clock is the globe's dataset video when it has one — `currentTime`,
 * play state and rate, read fresh each frame, so seeks, loops, meeting
 * and Khipu followers and Studio moves all reach the layer through the
 * video itself — or, for a video-less release (§10.3), the player's own
 * clock, which plays at 1× and loops after `timeTotalFrames` frames.
 *
 * Three things drive a frame:
 * - **the player's own animation-frame loop**, in the browser;
 * - **the headset's loop** (`tick`), because the window's animation frames
 *   stop in an immersive session; the headset draws `frameLayers` on its
 *   three.js globe (vrVectorLayers.ts);
 * - **a Studio render** (`renderStep`): the loop is off and each output
 *   frame advances the presentation clock by 1 / outputFps (§6).
 *
 * What a shown layer supersedes (§9) is reported to the caller, which
 * hides the dataset's video on the globe while the vector layer draws.
 * A click or tap on a drawn feature opens its inspection (§8) in a popup.
 * Labels (`style.label`, vectorLabels.ts) are drawn by the map layers.
 */
import * as maplibregl from 'maplibre-gl'
import type { Map as MaplibreMap, MapMouseEvent } from 'maplibre-gl'
import { logger } from '../../utils/logger'
import { streamHostBase } from '../streamHost'
import { prepareVectorLayer, type CompiledVectorLayer, type DsaVectors, type VectorFeature } from './vectorModel'
import { createVectorMapLayer, VECTOR_PICK_REACH_PX, type VectorMapLayerHandle } from './vectorMapLayer'
import { inspectFeature, pickVectorAt, type VectorFrameLayer, type VectorInspection, type VectorPick } from './vectorPick'
import { vectorClockOf, VectorTimeline, type VectorClock } from './vectorTimeline'

/** What the clock says this frame. */
export interface VectorClockReading {
  mediaTime: number
  playing: boolean
  rate: number
  atEnd: boolean
}

export type VectorClockSource = () => VectorClockReading | null

/** The dataset's video as the clock. */
export function videoClock(getVideo: () => HTMLVideoElement | null, clock: VectorClock | null): VectorClockSource {
  return () => {
    const v = getVideo()
    if (!v || v.readyState < 1) return null
    const fps = clock?.fps ?? 1
    const end = Number.isFinite(v.duration) && v.duration > 0 ? v.duration : (clock ? clock.frames / fps : Infinity)
    // The app holds the last frame before looping (playbackController's
    // LOOP_END_HOLD_MS): the end of the video counts as the end of the
    // timeline, so the last frame's features enter and play out.
    const atEnd = v.ended || v.currentTime >= end - 0.5 / fps
    return { mediaTime: v.currentTime, playing: (!v.paused || v.ended) && !v.seeking && v.playbackRate > 0, rate: v.playbackRate || 1, atEnd }
  }
}

/** A video-less clock (§6, §10.3): 1×, holding the end for `holdS`, then looping. */
export function ownClock(clock: VectorClock | null, holdS = 1): VectorClockSource & { pause(p: boolean): void } {
  let media = 0
  let held = 0
  let paused = false
  let last = performance.now()
  const duration = clock ? clock.frames / clock.fps : 0
  const source = (() => {
    const now = performance.now()
    const dt = Math.min(0.25, (now - last) / 1000)
    last = now
    if (!paused && duration > 0) {
      if (media >= duration) {
        held += dt
        if (held >= holdS) { media = 0; held = 0 }
      } else {
        media = Math.min(duration, media + dt)
      }
    }
    return { mediaTime: media, playing: !paused, rate: 1, atEnd: duration > 0 && media >= duration }
  }) as VectorClockSource & { pause(p: boolean): void }
  source.pause = (p: boolean) => { paused = p }
  return source
}

interface Shown {
  layer: CompiledVectorLayer
  timeline: VectorTimeline
  map: VectorMapLayerHandle
}

export interface VectorShowResult {
  shown: CompiledVectorLayer[]
  skipped: Array<{ order: number; reason: string }>
  /** Orders of the media layers the shown vector layers replace. */
  supersedes: Set<number>
  /** The dataset's video (its primary media layer) is superseded. */
  hidesVideo: boolean
  /** A legend image layer is superseded: show the vector legend instead. */
  hidesLegend: boolean
}

export interface VectorPlayerOptions {
  /** Runs first on every frame of the player's own loop: the host checks there what the globe should show. */
  onFrame?: () => void
  /** Whether a click on the globe may open an inspection now (no pin, line or game is being picked). */
  canInspect?: () => boolean
  /** The viewer's language, for the inspection (§8). */
  lang?: () => string
  /** Builds the popup's content; the default lists the title, fields and link. */
  renderInspection?: (inspection: VectorInspection) => HTMLElement
}

const EMPTY: VectorShowResult = { shown: [], skipped: [], supersedes: new Set(), hidesVideo: false, hidesLegend: false }

/** The popup's content: title, one row per field, and the link (§8). */
export function inspectionElement(inspection: VectorInspection, linkText = '↗'): HTMLElement {
  const box = document.createElement('div')
  box.className = 'vector-inspect'
  const title = document.createElement('div')
  title.className = 'vector-inspect-title'
  title.textContent = inspection.title
  box.appendChild(title)
  if (inspection.rows.length) {
    const list = document.createElement('dl')
    list.className = 'vector-inspect-fields'
    for (const row of inspection.rows) {
      const dt = document.createElement('dt')
      dt.textContent = row.label
      const dd = document.createElement('dd')
      dd.textContent = row.value
      list.append(dt, dd)
    }
    box.appendChild(list)
  }
  if (inspection.link) {
    const a = document.createElement('a')
    a.className = 'vector-inspect-link'
    a.href = inspection.link
    a.target = '_blank'
    a.rel = 'noopener noreferrer'
    a.textContent = linkText
    box.appendChild(a)
  }
  return box
}

export class VectorPlayer {
  private shown: Shown[] = []
  /** AYNI (owner, 2026-10-12): the dataset's transparency and eye, for every layer shown and those to come. */
  private userOpacity = 1
  private generation = 0
  private raf: number | null = null
  private lastFrame = 0
  private clockSource: VectorClockSource | null = null
  /** The shown DSA's data clock (§6), or null. */
  private clock: VectorClock | null = null
  private url: string | null = null
  private result: VectorShowResult = EMPTY
  private drawn: VectorFrameLayer[] = []
  private frameVersion = 0
  private manual = false
  private clickMap: MaplibreMap | null = null
  private popup: maplibregl.Popup | null = null
  private readonly onFrame?: () => void

  constructor(private readonly getMap: () => MaplibreMap | null, private readonly options: VectorPlayerOptions = {}) {
    this.onFrame = options.onFrame
  }

  /** The `.dsa` currently drawn, or null. */
  get dsaUrl(): string | null {
    return this.url
  }

  get layers(): CompiledVectorLayer[] {
    return this.shown.map((s) => s.layer)
  }

  /** What the last `show` drew and what it supersedes. */
  get state(): VectorShowResult {
    return this.result
  }

  /** The layers as the last frame drew them, in `order` (for the headset and for picking). */
  get frameLayers(): readonly VectorFrameLayer[] {
    return this.drawn
  }

  /** The shown DSA's data clock (§6), or null. */
  get dataClock(): VectorClock | null {
    return this.clock
  }

  /** The data time (Unix s) the last frame drew, or null before one or without a clock. */
  dataTime(): number | null {
    if (!this.clock) return null
    const timed = this.shown.find((s) => s.layer.spec.time.mode !== 'static')
    const T = timed?.timeline.dataTime
    return timed && this.drawn.length && typeof T === 'number' && Number.isFinite(T) ? T : null
  }

  /** The features a layer shows for data time T, as a paused globe draws them; a static layer's all. */
  featuresAt(layer: CompiledVectorLayer, T: number | null): VectorFeature[] {
    const shown = this.shown.find((s) => s.layer === layer)
    if (!shown) return []
    if (layer.spec.time.mode === 'static') return layer.features
    return T === null ? [] : shown.timeline.snapshotFeatures(T)
  }

  /** Bumped on every frame, so a reader can tell a new frame from the last. */
  get version(): number {
    return this.frameVersion
  }

  /**
   * Draws a `.dsa`'s vector layers, replacing what was there. Layers this
   * player cannot draw are skipped with a reason (§9); a later call wins
   * over an earlier one still loading.
   */
  async show(dsaUrl: string, vectors: DsaVectors, clock: (c: VectorClock | null) => VectorClockSource, fetchFn?: typeof fetch): Promise<VectorShowResult> {
    const gen = ++this.generation
    this.clear(false)
    this.url = dsaUrl
    const results = await Promise.all(vectors.layers.map((l) => prepareVectorLayer(l, vectors.clock, dsaUrl, fetchFn)))
    if (gen !== this.generation) return EMPTY
    const vclock = vectorClockOf(vectors.clock)
    const out: VectorShowResult = { shown: [], skipped: [], supersedes: new Set(), hidesVideo: false, hidesLegend: false }
    for (const r of results) {
      if (!r.ok) {
        out.skipped.push({ order: r.order, reason: r.reason })
        logger.warn(`[vector] layer ${r.order} of ${dsaUrl} skipped: ${r.reason}`)
        continue
      }
      if (r.layer.spec.time.mode !== 'static' && !vclock) {
        out.skipped.push({ order: r.layer.order, reason: 'no clock' })
        continue
      }
      const map = createVectorMapLayer(this.getMap, r.layer, () => this.options.lang?.() ?? 'en')
      map.setUserOpacity(this.userOpacity)
      this.shown.push({ layer: r.layer, timeline: new VectorTimeline(r.layer, vclock), map })
      out.shown.push(r.layer)
      for (const o of r.layer.supersedes) out.supersedes.add(o)
    }
    // Draw in `order`, so the stack matches the DSA's.
    this.shown.sort((a, b) => a.layer.order - b.layer.order)
    for (const o of out.supersedes) {
      const other = vectors.others.find((x) => x.order === o)
      if (!other) continue
      if (other.type === 'video' && !other.isLegend && !other.isPip) out.hidesVideo = true
      if (other.isLegend) out.hidesLegend = true
    }
    this.clockSource = clock(vclock)
    this.clock = vclock
    this.result = out
    if (this.shown.length) this.start()
    logger.info(`[vector] ${dsaUrl}: drawing ${out.shown.map((l) => `layer ${l.order} (${l.spec.geometry}/${l.spec.time.mode}, ${l.features.length} features)`).join(', ') || 'nothing'}${out.hidesVideo ? ', video superseded' : ''}`)
    return out
  }

  /**
   * Studio renders (§6): the player's own loop stops, and only
   * {@link renderStep} moves the clock, by the render's own time.
   */
  /** AYNI (owner, 2026-10-12): the dataset's transparency (1 opaque … 0 hidden) on the browser globe; the headset takes it from `getDatasetLook`. */
  setUserOpacity(opacity: number): void {
    this.userOpacity = Number.isFinite(opacity) ? Math.min(1, Math.max(0, opacity)) : 1
    for (const s of this.shown) s.map.setUserOpacity(this.userOpacity)
  }

  setManual(manual: boolean): void {
    if (manual === this.manual) return
    this.manual = manual
    if (manual) this.stop()
    else if (this.shown.length) this.start()
  }

  private start(): void {
    if (this.manual || this.raf !== null || typeof requestAnimationFrame === 'undefined') return
    this.lastFrame = performance.now()
    const loop = () => {
      this.raf = requestAnimationFrame(loop)
      try {
        this.onFrame?.()
        if (this.raf !== null) this.frame()
      } catch (err) { logger.warn('[vector] frame failed:', err) }
    }
    this.raf = requestAnimationFrame(loop)
  }

  private stop(): void {
    if (this.raf !== null) cancelAnimationFrame(this.raf)
    this.raf = null
  }

  /**
   * One frame from a loop other than the player's own (the headset's):
   * skipped when a frame already ran within the last few milliseconds, so
   * two loops running at once do not double the clock.
   */
  tick(now = performance.now()): void {
    if (this.manual || !this.shown.length) return
    if (now - this.lastFrame < 4) return
    try { this.frame(now) } catch (err) { logger.warn('[vector] frame failed:', err) }
  }

  /** One rendered frame: tick every layer's clock and draw it. */
  frame(now = performance.now()): void {
    const dt = Math.max(0, Math.min(0.25, (now - this.lastFrame) / 1000))
    this.lastFrame = now
    this.step(this.clockSource?.() ?? null, dt)
  }

  /**
   * A Studio render frame: `dt` presentation seconds since the last one
   * (1 / outputFps, or less between motion-blur samples), at the shot's
   * date `at.atMs` plus `at.playSeconds` of video played through a stop.
   * The date, not the video's time, is the data time: the video is seeked
   * to the frame holding the date, so within a frame its time stands still.
   * Playing means the date moved forward, at whatever rate the shot plays.
   */
  renderStep(dt: number, at?: { atMs: number | null; playSeconds: number }): void {
    const reading = this.clockSource?.() ?? null
    const clock = this.clock
    const media = at?.atMs != null && clock
      ? (at.atMs / 1000 - clock.startS) / (clock.fps * clock.cadence) + at.playSeconds
      : reading?.mediaTime ?? null
    const prev = this.lastRenderMedia
    this.lastRenderMedia = media
    if (media === null) { this.step(null, dt); return }
    const moved = prev !== null ? media - prev : 0
    const playing = prev !== null && moved > 0 && dt > 0
    const end = clock ? clock.frames / clock.fps : Infinity
    this.step({ mediaTime: Math.min(media, end), playing, rate: playing ? moved / dt : 1, atEnd: media >= end }, dt)
  }

  private lastRenderMedia: number | null = null

  private step(reading: VectorClockReading | null, dt: number): void {
    const drawn: VectorFrameLayer[] = []
    for (const s of this.shown) {
      const items = s.timeline.tick({
        mediaTime: reading?.mediaTime ?? 0, playing: reading?.playing ?? false, rate: reading?.rate ?? 1, dt, atEnd: reading?.atEnd,
      })
      s.map.update(items, s.timeline.dataTime)
      drawn.push({ layer: s.layer, items })
    }
    this.drawn = drawn
    this.frameVersion++
    this.wireClicks()
    if (this.shown.length && (reading?.playing || this.manual)) this.getMap()?.triggerRepaint()
  }

  /** The topmost drawn feature under a screen point of the browser globe. */
  pick(point: { x: number; y: number }, reachPx: number): VectorPick | null {
    for (let i = this.shown.length - 1; i >= 0; i--) {
      if (this.shown[i].layer.spec.interaction?.pickable === false) continue
      const f = this.shown[i].map.pick(point, reachPx)
      if (f) return { layer: this.shown[i].layer, feature: f }
    }
    return null
  }

  /** The topmost drawn feature at a place, for globes MapLibre does not draw (headset, phone AR). */
  pickAt(lat: number, lon: number, reachDeg: number): VectorPick | null {
    return pickVectorAt(this.drawn, lat, lon, reachDeg)
  }

  /** The inspection of a picked feature, in the viewer's language; a track's values where its head is drawn. */
  inspect(pick: VectorPick): VectorInspection {
    const item = pick.layer.spec.geometry === 'track'
      ? this.drawn.find((d) => d.layer === pick.layer)?.items.find((it) => it.feature === pick.feature)
      : undefined
    return inspectFeature(pick.layer, pick.feature, this.options.lang?.() ?? 'en', item?.headT)
  }

  /** Keeps one click handler on the globe the layers are drawn on. */
  private wireClicks(): void {
    const map = this.shown.length ? this.getMap() : null
    if (map === this.clickMap) return
    this.clickMap?.off('click', this.onClick)
    this.closePopup()
    this.clickMap = map
    map?.on('click', this.onClick)
  }

  private readonly onClick = (event: MapMouseEvent): void => {
    if (!this.shown.length || !(this.options.canInspect?.() ?? true)) return
    const map = this.clickMap
    if (!map) return
    // Off the globe, in space, MapLibre still puts the click on the horizon
    // and finds what is drawn there: a click counts only where the place
    // under it projects back to the pointer.
    const back = map.project(event.lngLat)
    if (Math.hypot(back.x - event.point.x, back.y - event.point.y) > 3) { this.closePopup(); return }
    const touch = (event.originalEvent as PointerEvent | undefined)?.pointerType === 'touch'
      || (typeof window !== 'undefined' && 'ontouchstart' in window && !window.matchMedia('(hover: hover)').matches)
    const hit = this.pick(event.point, touch ? VECTOR_PICK_REACH_PX.touch : VECTOR_PICK_REACH_PX.mouse)
    if (!hit) { this.closePopup(); return }
    const content = (this.options.renderInspection ?? inspectionElement)(this.inspect(hit))
    this.closePopup()
    this.popup = new maplibregl.Popup({ offset: 10, className: 'sos-popup vector-inspect-popup', focusAfterOpen: false, maxWidth: '18rem' })
      .setLngLat(event.lngLat)
      .setDOMContent(content)
      .addTo(map)
  }

  private closePopup(): void {
    this.popup?.remove()
    this.popup = null
  }

  /** Removes every layer; `bump` also cancels a show still loading. */
  clear(bump = true): void {
    if (bump) this.generation++
    this.stop()
    for (const s of this.shown) s.map.remove()
    this.shown = []
    this.drawn = []
    this.frameVersion++
    this.clockSource = null
    this.clock = null
    this.url = null
    this.result = EMPTY
    this.lastRenderMedia = null
    this.wireClicks()
  }
}

/**
 * Developer switches (not in the manual), from this site or the stream
 * host only:
 * - `?vectorPreview=<.dsa url>` draws that DSA's vector layers on the bare
 *   globe on their own clock (the published examples, which have no video);
 * - `?vectorDsa=<.dsa url>` draws that DSA's vector layers over the dataset
 *   of every globe, on its video's clock, in place of the dataset's own
 *   `.dsa` (the quakes `Test/` copy over production quakes).
 */
export function vectorPreviewUrl(
  search: string = typeof location === 'undefined' ? '' : location.search,
  origins: string[] = defaultOrigins(),
  param = 'vectorPreview',
): string | null {
  const raw = new URLSearchParams(search).get(param)
  if (!raw) return null
  try {
    const base = typeof location === 'undefined' ? 'http://localhost/' : location.href
    const url = new URL(raw, base)
    const allowed = origins.filter(Boolean).map((o) => new URL(o).origin)
    return allowed.includes(url.origin) ? url.toString() : null
  } catch {
    return null
  }
}

function defaultOrigins(): string[] {
  return [
    typeof location === 'undefined' ? '' : location.origin,
    streamHostBase(),
  ]
}
